import React, {useMemo} from "react";
import {Grid, Skeleton} from "@mui/material";
import {BrightnessLowOutlined, CheckCircleOutlined, EnergySavingsLeafOutlined, SsidChartOutlined} from "@mui/icons-material";
import {AProgressTotalizer} from "@atiautomacao/ati-ui-library";
import {differenceInCalendarDays} from "date-fns";
import {usePerformanceTotalizersQuery} from "./reactQuery/PerformanceTotalizersReactQuery";
import {PerformanceTotalizer} from "./PerformanceTotalizer.types";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";

const GENERATED_ENERGY_NAME = "Energia Gerada";
const IRRADIANCE_NAME = "Irradiação";
const PERFORMANCE_RATIO_NAME = "PR Médio";
const AVAILABILITY_NAME = "Disponibilidade";

interface PerformanceTotalizersProps {
    powerStationIds: number[];
    startDateTime: string | null;
    endDateTime: string | null;
}

const findByName = (totalizers: PerformanceTotalizer[] | undefined, name: string): PerformanceTotalizer | undefined =>
    totalizers?.find((totalizer) => totalizer.name === name);

export default function PerformanceTotalizers({powerStationIds, startDateTime, endDateTime}: PerformanceTotalizersProps) {
    const {data, isLoading, isError} = usePerformanceTotalizersQuery(powerStationIds, startDateTime, endDateTime);
    // Skeleton only on cold load — keep previous values during filter/period refetch (FR-014)
    const showSkeleton = isLoading && !data;

    const generatedEnergy = findByName(data, GENERATED_ENERGY_NAME);
    const irradiance = findByName(data, IRRADIANCE_NAME);
    const performanceRatio = findByName(data, PERFORMANCE_RATIO_NAME);
    const availability = findByName(data, AVAILABILITY_NAME);
    const lgSize = useMemo(() => {
        return startDateTime && endDateTime && differenceInCalendarDays(new Date(endDateTime), new Date(startDateTime)) > 29 ? 6 : 3;
    }, [startDateTime, endDateTime]);
  

    const cards = useMemo(
        () => [
            {
                key: GENERATED_ENERGY_NAME,
                label: GENERATED_ENERGY_NAME,
                value: truncateNumber((generatedEnergy?.generatedValue ?? 0), 2),
                target: truncateNumber((generatedEnergy?.expectedValue ?? 0), 2),
                progressValue: truncateNumber((generatedEnergy?.associationPercentage ?? 0), 2),
                limitMaxPercentage: 150,
                unit: "MWh",
                icon: EnergySavingsLeafOutlined,
            },
            {
                key: IRRADIANCE_NAME,
                label: IRRADIANCE_NAME,
                value: truncateNumber(irradiance?.generatedValue ?? 0, 2),
                target: truncateNumber(irradiance?.expectedValue ?? 0, 2),
                progressValue: truncateNumber(irradiance?.associationPercentage ?? 0, 2),
                limitMaxPercentage: 150,
                unit: "kWh/m²",
                icon: BrightnessLowOutlined,
            },
            {
                key: PERFORMANCE_RATIO_NAME,
                label: PERFORMANCE_RATIO_NAME,
                value: truncateNumber(performanceRatio?.generatedValue ?? 0, 2),
                target: truncateNumber(performanceRatio?.expectedValue ?? 0, 2),
                progressValue: truncateNumber(performanceRatio?.associationPercentage ?? 0, 2),
                limitMaxPercentage: 150,
                unit: "%",
                icon: SsidChartOutlined,
            },
            {
                key: AVAILABILITY_NAME,
                label: AVAILABILITY_NAME,
                value: truncateNumber(availability?.generatedValue ?? 0, 2),
                progressValue: truncateNumber(availability?.associationPercentage ?? 0, 2),
                unit: "%",
                icon: CheckCircleOutlined,
                showPercentage: false,
                
            },
        ],
        [generatedEnergy, irradiance, performanceRatio, availability]
    );

    if (isError) {
        return (
            <Grid container sx={{paddingLeft: 3, paddingBottom: 2}}>
                <DataNotFound boxStyle={{height: "150px", width: "100%"}}/>
            </Grid>
        );
    }

    return (
        <Grid key={lgSize} container spacing={2} sx={{paddingLeft: 3, paddingRight: 3, paddingBottom: 2}}>
            {cards.map((card) => (
                <Grid item xs={12} md={6} lg={lgSize} xl={3} key={card.key + lgSize}>
                    {showSkeleton ? (
                        <Skeleton animation="wave" height={92} variant="rounded"/>
                    ) : (
                        <AProgressTotalizer
                            sx={{height: 75}}
                            label={card.label}
                            value={card.value}
                            target={card.target}
                            progressValue={card.progressValue}
                            limitMaxPercentage={card.limitMaxPercentage}
                            showPercentage={card.showPercentage}
                            unit={card.unit}
                            icon={card.icon}
                        />
                    )}
                </Grid>
            ))}
        </Grid>
    );
}
